import type { FormState } from "./videoCreateTypes";
import type { FromPosterBootstrapResponse } from "../api/videoService";

// 契约 v2:lensMm/depthsOfField/colorTemperatures/textureKeywords 整组退休(导演不再产,
// 风格由 styleCode 一次定调);cameraSpeeds 补入 —— 它与 cameraMovements 同源同长,
// 漏列会让「新镜组到达时旧速度残留错位」这条既有防线对它失效。
const KEYS = [
  "shotSizes",
  "cameraMovements",
  "cameraSpeeds",
  "lightingKeys",
  "shotIntents",
  "narrativeRoles",
  "heroMoments",
  "sceneIds",
  "shotContinuities",
  // item1 事实→画面:per-shot 数组(与镜数对位),同样吃「新镜组长度失配即丢」。
  "factRefs",
  "factVisuals",
  // W3:逐镜三态与参考派活 —— 错位一格 = 口播镜静音、动作参考挂错镜,同吃对位丢弃。
  "speechModes",
  "references",
] as const;

// 批1:subjects(主体注册表)是 storyboard 级列表,长度与镜数无关 —— 不进 KEYS 的
// 长度对位丢弃逻辑,单独「非空覆盖/空保旧」。

/**
 * 从 bootstrap 取结构化富字段并入 form:非空数组覆盖;空/缺省时——
 * - b 带新分镜(panelScenes 非空)= 新镜组:旧富字段只有长度仍与新镜数对位才保留,否则丢弃。
 *   否则二次优化换了镜数后,旧镜组的 shotSizes/shotIntents 残留错位入库(2026-07-12 生产实证
 *   草稿 4351340314624:1 镜 panelScenes + 6 镜富字段)。
 * - b 不带分镜(纯装配 bootstrap)= 保旧(对齐 api ARRAY_FIELDS 语义)。
 */
export function mergeBootstrapRichFields(
  form: FormState,
  b: FromPosterBootstrapResponse
): Partial<FormState> {
  const newShotCount = b.panelScenes && b.panelScenes.length > 0 ? b.panelScenes.length : undefined;
  const out: Partial<FormState> = {};
  for (const k of KEYS) {
    const bv = (b as Record<string, unknown>)[k] as unknown[] | undefined;
    if (bv && bv.length > 0) {
      out[k] = bv as never;
      continue;
    }
    const old = form[k] as unknown[] | undefined;
    if (newShotCount !== undefined && old && old.length !== newShotCount) {
      out[k] = undefined as never; // 旧镜组残留,长度失配即丢
      continue;
    }
    out[k] = old as never;
  }
  const subjects = (b as Record<string, unknown>).subjects as string[] | undefined;
  out.subjects = subjects && subjects.length > 0 ? subjects : form.subjects;
  const hookPlan = (b as Record<string, unknown>).hookPlan as Record<string, unknown> | undefined;
  out.hookPlan = hookPlan && typeof hookPlan === "object" ? hookPlan : form.hookPlan;
  // T2A 实体表:storyboard 级(长度与镜数无关,同 subjects)——非空覆盖 / 空保旧。分类器 compose
  // 前产的实体经 compose 回传到这里,并入 form 供确认单展示 + createTask 透传给模式路由器。
  const entities = (b as Record<string, unknown>).entities as FormState["entities"] | undefined;
  out.entities = entities && entities.length > 0 ? entities : form.entities;
  return out;
}

// 基础 per-shot 数组(applyBootstrap 逐字段合并的那批):与富字段同规则对位。
// panelScenes 本身是基准(它定义 n),不进这张表 —— 拿它跟自己比长度恒真,是句废话。
const BASE_SHOT_KEYS = [
  "panelMotions",
  "panelSubjectRefs",
  "panelDurations",
  "negativePrompts",
  "voiceovers",
  "voiceEmotions",
  "shotMode",
] as const;

/**
 * 新分镜到达后的最终对位:任何 per-shot 数组长度 ≠ panelScenes 长度 → 丢弃(走标量/默认兜底)。
 * applyBootstrap 逐字段「空保旧」合并后调用,兜住旧镜组残留(与 mergeBootstrapRichFields 同因)。
 */
export function alignPerShotArrays(next: FormState): FormState {
  const n = next.panelScenes?.length ?? 0;
  if (n === 0) return next;
  const out = { ...next } as FormState & Record<string, unknown>;
  for (const k of [...KEYS, ...BASE_SHOT_KEYS]) {
    const v = out[k] as unknown[] | undefined;
    if (v && v.length !== n) out[k] = undefined;
  }
  return out;
}
